import React, { useEffect, useState } from 'react';
import GoogleAddress from '../GoogleAddress';
import StarRating from '../StarRating';
import NPSInput from '../NPSInput';
import { ENGAGEMENT_TIER } from '../../constants';
import AmazonObjectCreate from '../AmazonObjectCreate';
import { every } from 'lodash';
import ReactGA from 'react-ga';
import Input from '../../components/Form/Input';
import { getContrast } from '../../helpers/commonHelper';
import { COLORS } from '../../constants';

import { Dropdown, Label, Icon } from 'semantic-ui-react';
import {
  email,
  required,
  fullNameRequired,
  phoneRequired,
  emailRequired,
  birthDateRequired,
  socialMediaHandleRequired,
  paypalOrVenmoRequired,
  influencerFeeRequired,
} from '../../utils/validations';

import './style.css';

export const CampaignForm = ({
  inputFields,
  handleOnChange,
  handleGoogleAddress,
  values,
  campaignColor,
  campaignId,
  onFinishSelfie,
  onFinishProductPhoto,
  onAmazonUploadStart,
}) => {
  const inputValues = {
    Name: {
      name: 'fullName',
      type: 'text',
      placeholder: 'Full Name*',
      value: values.fullName,
      onChange: handleOnChange,
      validate: [fullNameRequired],
    },
    Email: {
      type: 'email',
      placeholder: 'Email*',
      name: 'email',
      value: values.email,
      onChange: handleOnChange,
      validate: [emailRequired, email],
    },
    Address: {
      type: 'address',
      name: 'address',
      placeholder: 'Address*',
      onChange: handleGoogleAddress,
    },
    Phone: {
      type: 'text',
      placeholder: 'Phone Number*',
      name: 'phoneNumber',
      onChange: handleOnChange,
      value: values.phoneNumber,
      validate: [phoneRequired],
    },
    'Birth Date (ex: Jan. 3, 1982)': {
      type: 'text',
      placeholder: 'Birth Date (ex: Jan. 3, 1982)*',
      name: 'birthDate',
      onChange: handleOnChange,
      value: values.birthDate,
      validate: [birthDateRequired],
    },
    'PayPal or Venmo': {
      type: 'text',
      placeholder: 'PayPal or Venmo*',
      name: 'paypalOrVenmo',
      onChange: handleOnChange,
      value: values.paypalOrVenmo,
      validate: [paypalOrVenmoRequired],
    },
    'Social Media Handle': {
      type: 'text',
      placeholder: 'Social Media Handle*',
      name: 'socialMediaHandle',
      onChange: handleOnChange,
      value: values.socialMediaHandle,
      validate: [socialMediaHandleRequired],
    },
    'Influencer Fee': {
      type: 'text',
      placeholder: 'Influencer Fee*',
      name: 'influencerFee',
      onChange: handleOnChange,
      value: values.influencerFee,
      validate: [influencerFeeRequired],
    },
    'Company/Organization Name': {
      type: 'text',
      placeholder: 'Company/Organization Name*',
      name: 'companyName',
      value: values.companyName,
      onChange: handleOnChange,
      validate: [required],
    },
    'Job Title': {
      type: 'text',
      placeholder: 'Job Title*',
      name: 'jobTitle',
      value: values.jobTitle,
      onChange: handleOnChange,
      validate: [required],
    },
    Country: {
      type: 'address',
      name: 'country',
      placeholder: 'Country*',
      onChange: handleGoogleAddress,
    },
    "Link to Your Company's Response to COVID-19": {
      type: 'text',
      placeholder: "Link to Your Company's Response to COVID-19*",
      name: 'covidResponseLink',
      value: values.covidResponseLink,
      onChange: handleOnChange,
      validate: [required],
    },
    'Institution Name': {
      type: 'text',
      placeholder: 'Institution Name*',
      name: 'institutionName',
      value: values.institutionName,
      onChange: handleOnChange,
      validate: [required],
    },
    'Engagement Tier': {
      type: 'select',
      name: 'engagementTier',
      placeholder: 'Engagement Tier*',
      options: ENGAGEMENT_TIER,
      validate: [required],
      onChange: handleOnChange,
    },
    'Star Ratings': {
      type: 'starts',
      name: 'starRating',
      onChange: handleOnChange,
    },
    NPS: {
      type: 'nps',
      name: 'nps',
      onChange: handleOnChange,
    },
  };

  const inputFileValues = {
    'Selfie Photo': {
      type: 'file',
      directoryPath: 'campaign/selfie_photo/original/' + campaignId,
      label: 'Upload Selfie*',
      onFinish: onFinishSelfie,
      onStart: onAmazonUploadStart,
      value: values.selfie_photo,
    },
    'Product Photo': {
      type: 'file',
      directoryPath: 'campaign/product_photo/original/' + campaignId,
      label: 'Upload Product Photo*',
      onFinish: onFinishProductPhoto,
      onStart: onAmazonUploadStart,
      value: values.product_photo,
    },
  };

  const [formAlreadyChanged, setFormAlreadyChanged] = useState(false);

  // start - so that analytics is computed only on the first interaction with the form
  useEffect(() => {
    const newValues = values ? { ...values } : {};
    delete newValues.selfie_photo;
    delete newValues.product_photo;

    if (!every(newValues, (value) => !value)) {
      setFormAlreadyChanged(true);
    }
  }, [values]);

  useEffect(() => {
    if (formAlreadyChanged) {
      ReactGA.event({
        category: 'Form',
        action: 'Interaction with the form',
      });
    }
  }, [formAlreadyChanged]);
  // end

  const contrastColor =
    getContrast(campaignColor) === COLORS.white
      ? 'white'
      : 'black';

  const uploadButtonStyle = { background: campaignColor, color: contrastColor }

  return (
    <div className='campaign-form'>
      {inputFields.map((key) => {
        const field = inputValues[key];
        if (field)
          return (
            <React.Fragment key={key}>
              {(field.type === 'text' || field.type === 'email') && (
                <Input
                  name={field.name}
                  type={field.type}
                  placeholder={field.placeholder}
                  value={field.value}
                  onChange={(e) => field.onChange(field.name, e.target.value)}
                  validate={field.validate}
                />
              )}

              {field.type === 'address' && (
                <GoogleAddress
                  placeholder={field.placeholder}
                  handleGoogleAddress={(address, lat, lng) =>
                    field.onChange(field.name, address, lat, lng)
                  }
                />
              )}

              {field.type === 'select' && (
                <Dropdown
                  className='mt10'
                  fluid
                  selection
                  name={field.type}
                  placeholder={field.placeholder}
                  options={field.options}
                  validate={field.validate}
                  onChange={(e, data) => field.onChange(field.name, data.value)}
                />
              )}

              {field.type === 'starts' && (
                <StarRating
                  handleSelected={(value) => field.onChange(field.name, value)}
                />
              )}

              {field.type === 'nps' && (
                <NPSInput
                  handleSelected={(value) => field.onChange(field.name, value)}
                />
              )}
            </React.Fragment>
          );
      })}

      <div className='photo-section'>
        {['Selfie Photo', 'Product Photo'].map((key) => {
          const field = inputFileValues[key];

            console.log({inputFields})
          if (field && inputFields.includes(key)) {
            return (
              <AmazonObjectCreate
                key={campaignId}
                directoryPath={field.directoryPath}
                onFinish={field.onFinish}
                onStart={field.onStart}
              >
                <span>
                  <Label style={uploadButtonStyle}>
                    <Icon name='upload' /> {field.label}
                  </Label>
                  {field.value && (
                    <Icon className='greenCheck' name='checkmark' />
                  )}
                </span>
              </AmazonObjectCreate>
            );
          }
        })}
      </div>

      <p>Note: All fields marked as (*) are required</p>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/CampaignForm/index.jsx